// src/utils/wakeLock.js
//
// Best-effort screen wake-lock. Uses the standard Wake Lock API when
// available; falls back to a tiny muted looping <video> on iOS Safari.
// (Drop /public/nosleep.mp4 to enable the fallback.)

let wakeLockSentinel = null;
let isEnabled = false;

export function isWakeLockSupported() {
  return typeof navigator !== "undefined" && "wakeLock" in navigator;
}

let noSleepVideoEl = null;
let noSleepVideoEnabled = false;

async function ensureNoSleepVideoElement() {
  if (typeof document === "undefined") return null;
  if (noSleepVideoEl) return noSleepVideoEl;

  const v = document.createElement("video");
  v.setAttribute("playsinline", "true");
  v.setAttribute("muted", "true");
  v.muted = true;
  v.loop = true;
  v.src = "/nosleep.mp4";

  v.style.position = "fixed";
  v.style.left = "-9999px";
  v.style.top = "-9999px";
  v.style.width = "1px";
  v.style.height = "1px";
  v.style.opacity = "0";
  v.style.pointerEvents = "none";

  document.body.appendChild(v);
  noSleepVideoEl = v;
  return v;
}

async function requestNoSleepVideo() {
  try {
    const v = await ensureNoSleepVideoElement();
    if (!v) return false;
    await v.play();
    noSleepVideoEnabled = true;
    return true;
  } catch {
    noSleepVideoEnabled = false;
    return false;
  }
}

export async function requestWakeLock() {
  if (typeof document === "undefined") return false;
  if (document.visibilityState !== "visible") return false;

  if (!isWakeLockSupported()) return requestNoSleepVideo();

  if (wakeLockSentinel) return true;

  try {
    wakeLockSentinel = await navigator.wakeLock.request("screen");

    wakeLockSentinel.addEventListener("release", () => {
      wakeLockSentinel = null;
    });
    return true;
  } catch {
    wakeLockSentinel = null;
    return false;
  }
}

export async function releaseWakeLock() {
  try {
    if (wakeLockSentinel) await wakeLockSentinel.release();
  } catch {} finally {
    wakeLockSentinel = null;
  }
  try {
    if (noSleepVideoEl) {
      noSleepVideoEl.pause();
      noSleepVideoEl.currentTime = 0;
    }
  } catch {} finally {
    noSleepVideoEnabled = false;
  }
}

export function enableWakeLockBestEffort() {
  if (isEnabled) return;
  isEnabled = true;

  const tryAcquire = async () => { await requestWakeLock(); };
  tryAcquire();

  const onVisibility = () => {
    if (document.visibilityState === "visible") tryAcquire();
    else releaseWakeLock();
  };
  const onFocus = () => {
    if (document.visibilityState === "visible") tryAcquire();
  };
  const onPageShow = () => {
    if (document.visibilityState === "visible") tryAcquire();
  };
  const onAnyGesture = () => {
    if (document.visibilityState !== "visible") return;
    if (wakeLockSentinel) return;
    if (!isWakeLockSupported() && noSleepVideoEnabled) return;
    tryAcquire();
  };

  let heartbeat = null;
  const startHeartbeat = () => {
    if (heartbeat) return;
    heartbeat = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (wakeLockSentinel) return;
      if (!isWakeLockSupported() && noSleepVideoEnabled) return;
      tryAcquire();
    }, 2000);
  };
  const stopHeartbeat = () => {
    if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", onFocus);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("pointerdown", onAnyGesture, true);
  window.addEventListener("keydown", onAnyGesture, true);

  startHeartbeat();

  window.__museumxWakeLockCleanup = () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("pointerdown", onAnyGesture, true);
    window.removeEventListener("keydown", onAnyGesture, true);
    stopHeartbeat();
    releaseWakeLock();
    isEnabled = false;
  };
}

export function disableWakeLock() {
  if (typeof window !== "undefined" && window.__museumxWakeLockCleanup) {
    window.__museumxWakeLockCleanup();
    window.__museumxWakeLockCleanup = null;
  } else {
    releaseWakeLock();
    isEnabled = false;
  }
}
